<template>
  <MediaOwners
    :can-be-removed="canBeRemoved"
    :is-read-only="isReadOnly"
    :tiles-are-readonly="tilesAreReadOnly"
    :is-multiselect="lineItem?.purchaseType !== PurchaseTypes.GUARANTEED"
    :selected-media-owners-i-ds="selectedMediaOwnersIDs"
    :is-new="lineItem?.id === -1"
    @changed="onMediaOwnersSelectionChanged"
    @cleared="clear" />
</template>

<script setup lang="ts">
  import { computed, ComputedRef, toRefs } from 'vue';

  import PurchaseTypes from '@/common/enums/purchase-types.enum';
  import { LineItem } from '@/common/models';
  import { mapActions } from '@/utils/StateMapping';

  import MediaOwners from './MediaOwners.vue';

  interface IMediaOwnersProps {
    entity: LineItem;
    canBeRemoved: boolean;
    isReadOnly: boolean;
  }

  interface IMediaOwnerEmits {
    (e: 'changed', value: number[]): void;
    (e: 'cleared'): void;
  }

  // Props
  const props = defineProps<IMediaOwnersProps>();
  const { entity, canBeRemoved, isReadOnly } = toRefs(props);

  // Emits
  const emit = defineEmits<IMediaOwnerEmits>();

  // Computed

  const isLineItem: ComputedRef<boolean> = computed(() => entity.value.type === 'LineItem');
  const lineItem: ComputedRef<LineItem> = computed(() => (isLineItem.value ? entity.value : undefined));

  const isFromPackage: ComputedRef<boolean> = computed(() => lineItem.value.packageID != null);

  const tilesAreReadOnly: ComputedRef<boolean> = computed(
    () =>
      isReadOnly.value ||
      isFromPackage.value ||
      lineItem.value.dealIDProperty != null ||
      lineItem.value.dealIDsProperty != null,
  );

  const selectedMediaOwnersIDs = computed(() => {
    if (lineItem.value == null || lineItem.value.mediaOwnerIDs == null) {
      return [];
    }
    return lineItem.value.mediaOwnerIDs.filter((x) => x != null);
  });

  // Methods
  const { updateLineItemMediaOwnerIDs } = mapActions();

  function updateMediaOwnerIDs(IDs: number[]) {
    if (!isLineItem.value) {
      return;
    }
    updateLineItemMediaOwnerIDs({ lineItem: lineItem.value, value: IDs });
  }

  function onMediaOwnersSelectionChanged(mediaOwnersIDs: number[]) {
    updateMediaOwnerIDs(mediaOwnersIDs);
    emit('changed', mediaOwnersIDs);
  }

  function clear(availableMediaOwnersIDs: number[]) {
    updateLineItemMediaOwnerIDs({ lineItem: lineItem.value, value: availableMediaOwnersIDs });
    emit('cleared');
  }
</script>
